// src/components/Packaging/PackagingLibrary.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePackaging } from "../../contexts/PackagingContext";
import { usePOs } from "../../contexts/POContext";
import PackagingItemModal from "./PackagingItemModal";

function num(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

// Compute the base per-unit price from an item (supports "unit" and "pack")
function perUnitBase(item) {
  const mode = item?.pricingMode || "unit";
  if (mode === "unit") return num(item?.unitPrice, 0);
  const packPrice = num(item?.packPrice, 0);
  const unitsPerPack = Math.max(1, num(item?.unitsPerPack, 1));
  return packPrice / unitsPerPack;
}

export default function PackagingLibrary() {
  const navigate = useNavigate();
  const { items = [], deleteItem } = usePackaging() || {};
  const { orders = [], addOrder } = usePOs();

  // -------- TAX VIEW STATE (synced from CustomizeModal via event) --------
  const [taxName, setTaxName] = useState("GST");
  const [taxRatePct, setTaxRatePct] = useState(10);
  const [taxModeDefault, setTaxModeDefault] = useState("inclusive"); // inclusive | exclusive | none
  const [displayTaxMode, setDisplayTaxMode] = useState("inclusive"); // UI toggle for viewing prices
  const [toastMessage, setToastMessage] = useState(null);

  useEffect(() => {
    const onTax = (e) => {
      const { taxName, taxRatePct, taxModeDefault } = e.detail || {};
      if (taxName) setTaxName(taxName);
      if (typeof taxRatePct === "number") setTaxRatePct(taxRatePct);
      if (taxModeDefault) {
        setTaxModeDefault(taxModeDefault);
        setDisplayTaxMode((prev) =>
          prev === "inclusive" || prev === "exclusive" || prev === "none"
            ? prev
            : taxModeDefault
        );
      }
    };
    document.addEventListener("mudo:taxSettingsChanged", onTax);
    return () => document.removeEventListener("mudo:taxSettingsChanged", onTax);
  }, []);

  // -------- FILTERS / SORT --------
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("name"); // name | price | category
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  const packagingRows = useMemo(() => {
  // Show ALL packaging items (don’t restrict to category === "Packaging")
    const all = Array.isArray(items) ? items : [];
    const query = q.trim().toLowerCase();
    const filtered = !query
      ? all
      : all.filter((i) => {
          const hay = [
            i?.name || "",
            i?.sku || "",
            i?.category || "",
            i?.supplier || i?.supplierName || "",
            i?.notes || i?.note || "",
          ]
            .join(" ")
            .toLowerCase();
          return hay.includes(query);
        });

    const dir = sortDir === "desc" ? -1 : 1;
    const sorted = filtered.slice().sort((a, b) => {
      if (sortKey === "price") {
        const va = perUnitBase(a);
        const vb = perUnitBase(b);
        return (va - vb) * dir;
      }
      if (sortKey === "category") {
        return String(a.category || "").localeCompare(String(b.category || "")) * dir;
      }
      return String(a.name || "").localeCompare(String(b.name || "")) * dir;
    });
    return sorted;
  }, [items, q, sortKey, sortDir]);

  // -------- MODAL --------
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const onAdd = () => {
    setEditing(null);
    setOpen(true);
  };
  const onEdit = (it) => {
    setEditing(it);
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
    setEditing(null);
  };

  // compute display price (inc/exc tax) respecting item.taxFlag if present
  const computeDisplayPrice = (item) => {
    const base = perUnitBase(item);
    const rate = Math.max(0, Number(taxRatePct) || 0) / 100;

    // Item-level flag wins; fallback to app default
    const itemTaxFlag = (item?.taxFlag || "").toLowerCase(); // inclusive | exclusive | none | ""
    const effectiveFlag = itemTaxFlag || taxModeDefault || "inclusive";

    const view = displayTaxMode; // how we show numbers in the table

    if (view === "none" || effectiveFlag === "none") return base;

    if (effectiveFlag === "inclusive" && view === "inclusive") return base;
    if (effectiveFlag === "exclusive" && view === "exclusive") return base;

    if (effectiveFlag === "inclusive" && view === "exclusive") {
      const divisor = 1 + rate;
      return divisor > 0 ? base / divisor : base;
    }
    if (effectiveFlag === "exclusive" && view === "inclusive") {
      return base * (1 + rate);
    }
    return base;
  };

  const remove = (row) => {
    if (typeof deleteItem === "function") {
      deleteItem(row.id);
    } else {
      alert("Delete not available — missing deleteItem in PackagingContext.");
    }
  };

  // Helper functions for reorder functionality
  const getStock = (item) => {
    const stock = item?.stock ?? item?.stockQty ?? item?.quantity ?? 0;
    return Number.isFinite(Number(stock)) ? Number(stock) : 0;
  };

  const getStockStatus = (item) => {
    const stock = getStock(item);
    const reorderPoint = Number(item?.lowStockLimit ?? item?.reorderPoint ?? item?.minStock ?? 0);
    
    if (stock === 0) return "OUT";
    if (reorderPoint > 0 && stock <= reorderPoint) return "LOW";
    return "OK";
  };

  const showToast = (message, action = null) => {
    setToastMessage({ message, action });
    setTimeout(() => setToastMessage(null), 5000);
  };

  const handleAddToPO = (item) => {
    try {
      // Find or create a Draft PO
      let draftPO = orders.find(po => po.status === "Draft" || po.status === "draft");
      
      const stock = getStock(item);
      const reorderPoint = Number(item?.lowStockLimit ?? item?.reorderPoint ?? item?.minStock ?? 1);
      const suggestedQty = Math.max(1, reorderPoint - stock);
      
      const unitCost = perUnitBase(item);

      const poLineItem = {
        id: `${Date.now()}-${Math.random()}`,
        materialId: item.id,
        name: item.name || "Unnamed",
        sku: item.sku || "",
        supplier: item.supplier || item.supplierName || "",
        website: item.link || item.website || "",
        unit: item.uom || item.trackingUnit || "each",
        unitCost: unitCost,
        qty: suggestedQty,
        category: item.category || "Packaging",
        notes: `Reorder: stock ${stock}, min ${reorderPoint}`
      };

      if (draftPO) {
        // Add to existing draft PO
        const updatedItems = [...(draftPO.items || []), poLineItem];
        addOrder({
          ...draftPO,
          items: updatedItems,
          updatedAt: Date.now()
        });
      } else {
        // Create new draft PO
        draftPO = {
          id: `PO-${Date.now()}`,
          status: "Draft",
          supplier: poLineItem.supplier,
          items: [poLineItem],
          createdAt: Date.now(),
          updatedAt: Date.now(),
          notes: "Auto-created from Packaging reorder"
        };
        addOrder(draftPO);
      }

      showToast(
        `Added "${item.name}" to Draft PO`,
        {
          label: "Open PO",
          onClick: () => navigate("/dashboard/purchase-orders")
        }
      );
    } catch (error) {
      console.error("Error adding to PO:", error);
      showToast("Failed to add to PO");
    }
  };

  const thBtn = (key, label) => (
    <button
      className="inline-flex items-center gap-1 group"
      onClick={() => {
        if (sortKey === key) {
          setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
          setSortKey(key);
          setSortDir("asc");
        }
      }}
      title={`Sort by ${label}`}
    >
      {label}
      <span className="opacity-50 group-hover:opacity-80 text-[10px]">
        {sortKey === key ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </button>
  );

  return (
    <div className="min-h-screen p-6 text-white bg-neutral-900">
      {/* title */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold">Packaging Library</h1>
          <p className="text-xs opacity-70">
            Items here are used by packaging rules and costing. Prices shown{" "}
            <span className="opacity-100 underline">
              {displayTaxMode === "inclusive"
                ? `incl. ${taxName} (${taxRatePct}%)`
                : displayTaxMode === "exclusive"
                ? `excl. ${taxName} (${taxRatePct}%)`
                : "with no tax"}
            </span>
            .
          </p>
        </div>
        <Link
          to="/dashboard"
          className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 text-sm"
        >
          ← Back to Dashboard
        </Link>
      </div>

      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex-1 min-w-[220px]">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, SKU, supplier…"
            className="w-full px-3 py-2 rounded bg-neutral-800 border border-white/10"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs opacity-80">View prices:</label>
          <select
            value={displayTaxMode}
            onChange={(e) => setDisplayTaxMode(e.target.value)}
            className="px-2 py-1 rounded bg-neutral-800 border border-white/10 text-sm"
          >
            <option value="inclusive">Inclusive</option>
            <option value="exclusive">Exclusive</option>
            <option value="none">No tax</option>
          </select>
        </div>

        <button
          onClick={onAdd}
          className="px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-sm text-white"
        >
          + Add Item
        </button>
      </div>

      {/* table */}
      <div className="rounded-lg overflow-hidden border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-2 w-16">Image</th>
              <th className="text-left p-2">{thBtn("name", "Name")}</th>
              <th className="text-left p-2">SKU</th>
              <th className="text-left p-2">{thBtn("category", "Category")}</th>
              <th className="text-left p-2">UOM</th>
              <th className="text-right p-2">{thBtn("price", "Unit Price")}</th>
              <th className="text-left p-2">Supplier</th>
              <th className="text-left p-2">Link</th>
              <th className="text-left p-2">Notes</th>
              <th className="text-left p-2">Reorder</th>
              <th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {packagingRows.length === 0 ? (
              <tr>
                <td colSpan={11} className="p-4 text-center opacity-60">
                  No packaging items yet. Click "Add Item".
                </td>
              </tr>
            ) : (
              packagingRows.map((row) => {
                const base = perUnitBase(row);
                const show = computeDisplayPrice(row);
                const uom = row?.uom || row?.trackingUnit || "each";
                return (
                  <tr key={row.id} className="odd:bg-white/0 even:bg-white/[0.03]">
                    <td className="p-2">
                      {row.image ? (
                        <img
                          src={row.image}
                          alt={row.name || "Packaging"}
                          className="w-12 h-12 object-cover rounded border border-white/10"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded border border-white/10 bg-white/5 flex items-center justify-center text-white/30 text-xs">
                          No img
                        </div>
                      )}
                    </td>
                    <td className="p-2">{row.name || "Untitled"}</td>
                    <td className="p-2">{row.sku || <span className="opacity-50">—</span>}</td>
                    <td className="p-2">{row.category || "Packaging"}</td>
                    <td className="p-2">{uom}</td>
                    <td className="p-2 text-right">
                      <div className="leading-tight">
                        <div>${show.toFixed(4)} <span className="opacity-60">/ {uom}</span></div>
                        <div className="text-[11px] opacity-60">
                          Base: ${base.toFixed(4)}
                          {row?.taxFlag
                            ? ` • ${row.taxFlag.toLowerCase() === "inclusive" ? "incl" : row.taxFlag.toLowerCase() === "exclusive" ? "excl" : "no"} ${taxName}`
                            : ` • default ${taxModeDefault}`}
                        </div>
                      </div>
                    </td>
                    <td className="p-2">{row.supplier || row.supplierName || <span className="opacity-50">—</span>}</td>
                    <td className="p-2">
                      {row.link || row.supplierLink ? (
                        <a
                          href={row.link || row.supplierLink}
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          {row.link || row.supplierLink}
                        </a>
                      ) : (
                        <span className="opacity-50">—</span>
                      )}
                    </td>
                    <td className="p-2">{row.notes || row.note || <span className="opacity-50">—</span>}</td>
                    <td className="p-2">
                      {(() => {
                        const status = getStockStatus(row);
                        const needsReorder = status === "LOW" || status === "OUT";
                        
                        return (
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-block text-[10px] leading-none px-2 py-1 rounded-full border font-medium ${
                                status === "OUT"
                                  ? "border-red-400/50 bg-red-500/20 text-red-300"
                                  : status === "LOW"
                                  ? "border-amber-400/50 bg-amber-500/20 text-amber-300"
                                  : "border-gray-500/50 bg-gray-700/30 text-gray-400"
                              }`}
                            >
                              {status}
                            </span>
                            
                            {needsReorder && (
                              <button
                                onClick={() => handleAddToPO(row)}
                                className="px-2 py-1 rounded text-[10px] font-medium
                                  border border-blue-400/40 bg-blue-500/20 text-blue-300
                                  hover:bg-blue-500/30 hover:border-blue-400/60
                                  transition-colors"
                                title="Add to Purchase Order"
                              >
                                Add to PO
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-2 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => onEdit(row)}
                          className="px-2 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => remove(row)}
                          className="px-2 py-1 rounded bg-red-600 hover:bg-red-500 text-white"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* modal */}
      {open && (
        <PackagingItemModal
          open={open}
          onClose={onClose}
          initialItem={editing || undefined}
          categoryOptions={["Box", "Protective", "Label", "Insert", "Filler"]}
        />
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-5">
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl
            bg-zinc-800 border border-white/20 text-white">
            <span className="text-sm">{toastMessage.message}</span>
            {toastMessage.action && (
              <button
                onClick={toastMessage.action.onClick}
                className="px-3 py-1 rounded text-xs font-medium
                  bg-blue-500 text-white hover:bg-blue-600
                  transition-colors"
              >
                {toastMessage.action.label}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
